import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useFinanceFilters, FinanceFilterBar, MKT_LABELS } from '../lib/FinanceFilters.js';

interface Account { id: string; name: string; is_active?: number }

interface Settlement {
  settlementId: string; marketplace: string; currency: string;
  periodStart: string; periodEnd: string; depositDate: string;
  sales: number; fees: number; ppc: number; promos: number;
  reimbursements: number; netDeposit: number; netDepositUsd: number;
  status: 'deposited' | 'pending';
}

interface MonthRow {
  month: string; revenue: number; fees: number; ppc: number; cogs: number;
  refunds: number; promos: number; netProfit: number; totalSettled: number; pending: number;
}

interface DayPoint { date: string; revenue: number; costs: number; profit: number; settlementDeposit: number }

interface CashflowData {
  position: {
    pendingRevenue: number; pendingByMarket: Array<{ marketplace: string; amount: number; orders: number }>;
    nextDepositEta: string | null; nextDepositAmount: number; last30dNetCash: number; avgCycleDays: number;
  };
  settlements: Settlement[];
  monthly: MonthRow[];
  dailySeries: DayPoint[];
}

export function FinanceCashflow() {
  const [data, setData] = useState<CashflowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const filters = useFinanceFilters('90');

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const accts = await api.get<Account[]>('/accounts');
        const active = accts.filter(a => a.is_active !== 0);
        if (active.length === 0) { setLoading(false); return; }

        const mktParam = filters.marketplace ? `&marketplace=${filters.marketplace}` : '';
        const results = await Promise.all(
          active.map(a => api.get<CashflowData>(`/finance/cashflow/${a.id}?days=${filters.period}${filters.dateParam}${mktParam}`).catch(() => null)),
        );

        // Merge from all accounts
        const merged: CashflowData = {
          position: { pendingRevenue: 0, pendingByMarket: [], nextDepositEta: null, nextDepositAmount: 0, last30dNetCash: 0, avgCycleDays: 14 },
          settlements: [], monthly: [], dailySeries: [],
        };
        for (const r of results) {
          if (!r) continue;
          merged.position.pendingRevenue += r.position.pendingRevenue;
          merged.position.pendingByMarket.push(...r.position.pendingByMarket);
          merged.position.nextDepositAmount += r.position.nextDepositAmount;
          merged.position.last30dNetCash += r.position.last30dNetCash;
          if (r.position.nextDepositEta && (!merged.position.nextDepositEta || r.position.nextDepositEta < merged.position.nextDepositEta)) {
            merged.position.nextDepositEta = r.position.nextDepositEta;
          }
          merged.position.avgCycleDays = r.position.avgCycleDays;
          merged.settlements.push(...r.settlements);
          // Merge monthly by month key
          for (const m of r.monthly) {
            const ex = merged.monthly.find(x => x.month === m.month);
            if (ex) {
              ex.revenue += m.revenue; ex.fees += m.fees; ex.ppc += m.ppc;
              ex.cogs += m.cogs; ex.refunds += m.refunds; ex.promos += m.promos;
              ex.netProfit += m.netProfit; ex.totalSettled += m.totalSettled; ex.pending += m.pending;
            } else {
              merged.monthly.push({ ...m });
            }
          }
          // Merge daily series by date
          for (const d of r.dailySeries) {
            const ex = merged.dailySeries.find(x => x.date === d.date);
            if (ex) { ex.revenue += d.revenue; ex.costs += d.costs; ex.profit += d.profit; ex.settlementDeposit += d.settlementDeposit; }
            else merged.dailySeries.push({ ...d });
          }
        }
        merged.settlements.sort((a, b) => (b.depositDate || b.periodEnd).localeCompare(a.depositDate || a.periodEnd));
        merged.monthly.sort((a, b) => b.month.localeCompare(a.month));
        merged.dailySeries.sort((a, b) => a.date.localeCompare(b.date));

        // Collect marketplaces
        const mktSet = new Set<string>();
        for (const s of merged.settlements) {
          const mpId = Object.entries(MKT_LABELS).find(([, v]) => v === s.marketplace)?.[0];
          if (mpId) mktSet.add(mpId);
        }
        if (!filters.marketplace) filters.setMarkets([...mktSet].sort());

        setData(merged);
      } catch (e) { setError(String(e)); }
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.period, filters.marketplace, filters.startDate, filters.endDate]);

  const fmt = (v: number) => {
    const abs = Math.abs(v);
    const s = abs >= 1000 ? abs.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : abs.toFixed(2);
    return `${v < 0 ? '-' : ''}$${s}`;
  };

  if (loading) return <div className="py-12 text-center text-gray-400 animate-pulse">Loading cash flow...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cash Flow</h1>
        <p className="mt-1 text-sm text-gray-500">Settlement deposits, pending revenue, and cash position</p>
      </div>

      <FinanceFilterBar filters={filters} showCurrency={false} />

      {error && <p className="text-sm text-red-500">{error}</p>}
      {!data ? <p className="py-8 text-center text-gray-400">No cash flow data available</p> : (
        <>
          {/* ── Section 1: Cash Position Cards ── */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Card label="Pending Revenue" value={fmt(data.position.pendingRevenue)}
              sub={data.position.pendingByMarket.map(p => `${p.marketplace}: ${fmt(Math.round(p.amount))}`).join(' · ')} />
            <Card label="Next Deposit ETA" value={data.position.nextDepositEta ?? '—'}
              sub={`Est. ${fmt(data.position.nextDepositAmount)}`} color="blue" />
            <Card label="Period Net Cash" value={fmt(data.position.last30dNetCash)}
              sub={`${filters.startDate} → ${filters.endDate}`}
              color={data.position.last30dNetCash >= 0 ? 'green' : 'red'} />
            <Card label="Settlement Cycle" value={`${data.position.avgCycleDays} days`}
              sub="avg between deposits" />
            <Card label="Total Settled" value={fmt(data.settlements.reduce((s, x) => s + x.netDepositUsd, 0))}
              sub={`${data.settlements.length} settlements in period`} />
          </div>

          {/* ── Section 2: Cash Flow Chart ── */}
          {data.dailySeries.length > 1 && <CashFlowChart series={data.dailySeries} fmt={fmt} />}

          {/* ── Section 3: Settlement Timeline ── */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Settlement Timeline</h3>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10 border-b border-gray-200">
                  <tr>
                    <th className="pb-2 text-left text-xs font-medium text-gray-500">Market</th>
                    <th className="pb-2 text-left text-xs font-medium text-gray-500">Period</th>
                    <th className="pb-2 text-left text-xs font-medium text-gray-500">Deposit</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Sales</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Fees</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">PPC</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Promos</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Net Deposit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.settlements.map((s, i) => {
                    const cur = s.currency === 'USD' ? '$' : s.currency === 'CAD' ? 'C$' : s.currency === 'GBP' ? '£' : s.currency === 'MXN' ? 'MX$' : s.currency;
                    const f = (v: number) => `${v < 0 ? '-' : ''}${cur}${Math.abs(v).toFixed(0)}`;
                    return (
                      <tr key={`${s.settlementId}-${s.marketplace}-${i}`} className={s.status === 'pending' ? 'bg-yellow-50' : ''}>
                        <td className="py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            s.marketplace === 'US' ? 'bg-blue-100 text-blue-700' :
                            s.marketplace === 'CA' ? 'bg-red-100 text-red-700' :
                            s.marketplace === 'UK' ? 'bg-purple-100 text-purple-700' :
                            s.marketplace === 'MX' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>{s.marketplace}</span>
                        </td>
                        <td className="py-2 text-xs text-gray-600">{s.periodStart} → {s.periodEnd}</td>
                        <td className="py-2 text-xs text-gray-700">{s.depositDate || <span className="text-yellow-600">pending</span>}</td>
                        <td className="py-2 text-right text-xs text-gray-700">{f(s.sales)}</td>
                        <td className="py-2 text-right text-xs text-red-600">{f(s.fees)}</td>
                        <td className="py-2 text-right text-xs text-red-600">{s.ppc !== 0 ? f(s.ppc) : '—'}</td>
                        <td className="py-2 text-right text-xs text-red-600">{s.promos !== 0 ? f(s.promos) : '—'}</td>
                        <td className={`py-2 text-right text-xs font-medium ${s.netDeposit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {f(s.netDeposit)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Section 4: Monthly Cash Summary ── */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Monthly Summary (USD)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200">
                  <tr>
                    <th className="pb-2 text-left text-xs font-medium text-gray-500">Month</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Revenue</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Fees</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">PPC</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">COGS</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Refunds</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Net Profit</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Settled</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.monthly.map(m => (
                    <tr key={m.month}>
                      <td className="py-2 text-gray-700 font-medium">{m.month}</td>
                      <td className="py-2 text-right text-gray-700">{fmt(m.revenue)}</td>
                      <td className="py-2 text-right text-red-500">{fmt(m.fees)}</td>
                      <td className="py-2 text-right text-red-500">{fmt(m.ppc)}</td>
                      <td className="py-2 text-right text-gray-500">{fmt(m.cogs)}</td>
                      <td className="py-2 text-right text-gray-500">{m.refunds > 0 ? fmt(m.refunds) : '—'}</td>
                      <td className={`py-2 text-right font-medium ${m.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(m.netProfit)}</td>
                      <td className="py-2 text-right text-blue-600">{m.totalSettled > 0 ? fmt(m.totalSettled) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Cash Flow Chart (SVG) ──────────────────────────────────────────────────

function CashFlowChart({ series }: { series: DayPoint[]; fmt?: (v: number) => string }) {
  const W = 800, H = 180, PAD = 4;
  const maxVal = Math.max(...series.map(d => Math.max(d.revenue, d.costs, d.settlementDeposit)), 1);
  const x = (i: number) => PAD + (i / Math.max(series.length - 1, 1)) * (W - PAD * 2);
  const y = (v: number) => PAD + (1 - v / maxVal) * (H - PAD * 2);

  const revPoints = series.map((d, i) => `${x(i)},${y(d.revenue)}`).join(' ');
  const costPoints = series.map((d, i) => `${x(i)},${y(d.costs)}`).join(' ');
  const revArea = `${x(0)},${y(0)} ${revPoints} ${x(series.length - 1)},${y(0)}`;

  // Settlement deposit markers
  const deposits = series.map((d, i) => d.settlementDeposit > 0 ? { x: x(i), amt: d.settlementDeposit, date: d.date } : null).filter(Boolean) as Array<{ x: number; amt: number; date: string }>;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Cash Flow Trend</h3>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44" preserveAspectRatio="none">
        <polygon points={revArea} fill="rgba(147,197,253,0.2)" />
        <polyline points={revPoints} fill="none" stroke="#3b82f6" strokeWidth="1.5" />
        <polyline points={costPoints} fill="none" stroke="#ef4444" strokeWidth="1" strokeDasharray="4,3" />
        {/* Settlement deposit markers */}
        {deposits.map((d, i) => (
          <g key={i}>
            <line x1={d.x} y1={y(0)} x2={d.x} y2={y(d.amt)} stroke="#22c55e" strokeWidth="2" opacity="0.7" />
            <circle cx={d.x} cy={y(d.amt)} r="3" fill="#22c55e" />
          </g>
        ))}
      </svg>
      <div className="flex justify-between mt-1 text-[10px] text-gray-400">
        <span>{series[0]?.date.slice(5)}</span>
        <span className="flex gap-4">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-blue-400" /> Revenue</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-red-400 border-dashed" /> Costs</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-green-500" /> Settlement</span>
        </span>
        <span>{series[series.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}

// ── KPI Card ────────────────────────────────────────────────────────────────

function Card({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  const tc = color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-600' : color === 'blue' ? 'text-blue-600' : 'text-gray-900';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${tc}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-gray-400 truncate">{sub}</p>}
    </div>
  );
}
